/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { X } from 'lucide-react';
import { ChipSelectSearch } from '../../../components/muiCustomized/ChipSelectSearchable';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../../utils/styledSelect';

const FREQUENCY = ['Daily', 'Weekly', 'Monthly'];

function EditModal({
  closeModal,
  teams,
  users,
  selectedEmail,
  onSaved,
}) {
  const [emailName, setEmailName] = useState('');
  const [assignedTeams, setAssignedTeams] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [sendFrequency, setSendFrequency] = useState(-1);

  const isNew = selectedEmail.id === -1;

  useEffect(() => {
    if (isNew) return;

    setEmailName(selectedEmail.emailSubject);
    setSendFrequency(selectedEmail.sendFrequency);
    const combinedRecipients = selectedEmail.recipients.split(',');
    const combinedTeams = selectedEmail.teams.split(',');

    const updatedUsers = [];
    combinedRecipients.forEach((item) => {
      const found = users.find((u) => u.id === parseInt(item, 10));
      if (found) updatedUsers.push({ id: updatedUsers.length + 1, name: found.name });
    });
    setAssignedUsers(updatedUsers);

    const updatedTeams = [];
    combinedTeams.forEach((item) => {
      const found = teams.find((u) => u.id === parseInt(item, 10));
      if (found) updatedTeams.push({ id: updatedTeams.length + 1, name: found.name });
    });
    setAssignedTeams(updatedTeams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail]);

  const disableSave = !emailName || sendFrequency < 0;

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c">
      <Box style={{ width: 520, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, fontFamily: 'Inter, sans-serif' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h5 className="text-lg font-semibold leading-none text-ink-900">{isNew ? 'New Email' : 'Edit Email'}</h5>
          <button type="button" aria-label="Close" onClick={closeModal} className="text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer p-0">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-2 pb-5 flex flex-col gap-4">
          <div>
            <span className="block text-sm font-medium text-ink-900 mb-1.5">Email Name</span>
            <input
              type="text"
              value={emailName}
              onChange={(e) => setEmailName(e.target.value)}
              placeholder="Required"
              className="w-full box-border h-10 px-3 border border-rule rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500"
              style={{ borderStyle: 'solid' }}
            />
          </div>

          <ChipSelectSearch
            redesign
            id="teams"
            label="Recipients: Team / Teams"
            placeholder="Optional"
            count={`${assignedTeams.length}/${teams.length}`}
            selectedMembers={assignedTeams}
            members={teams}
            handleChange={(e) => setAssignedTeams(e.target.value)}
          />

          <ChipSelectSearch
            redesign
            id="users"
            label="Recipients: Individual"
            placeholder="Optional"
            count={`${assignedUsers.length}/${users.length}`}
            selectedMembers={assignedUsers}
            members={users}
            handleChange={(e) => setAssignedUsers(e.target.value)}
          />

          <div>
            <span className="block text-sm font-medium text-ink-900 mb-1.5">Send Frequency</span>
            <select
              value={sendFrequency}
              onChange={(e) => setSendFrequency(Number(e.target.value))}
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              <option value={-1} disabled>Required</option>
              {FREQUENCY.map((period, index) => <option key={period} value={index}>{period}</option>)}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-2 px-6 pb-5 pt-1">
          <button type="button" onClick={closeModal} className="inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>Cancel</button>
          <button
            type="button"
            disabled={disableSave}
            onClick={() => {
              onSaved(emailName, assignedTeams, assignedUsers, sendFrequency);
              closeModal();
            }}
            className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {isNew ? 'Compose Email' : 'Save Changes'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

EditModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })),
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })),
  selectedEmail: PropTypes.shape({
    id: PropTypes.number,
    emailId: PropTypes.number,
    isEnabled: PropTypes.number,
    emailSubject: PropTypes.string,
    sendFrequency: PropTypes.number,
    teams: PropTypes.string,
    recipients: PropTypes.string,
  }).isRequired,
  onSaved: PropTypes.func.isRequired,
};

EditModal.defaultProps = {
  teams: [],
  users: [],
};

export default EditModal;
